import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Shield, Book, Flame, AlertOctagon, Heart, Scale, Pencil, Trash2, Lock } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

export default function Codex() {
  const queryClient = useQueryClient();
  const [canEdit, setCanEdit] = React.useState(false);
  
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: () => base44.auth.me() });

  React.useEffect(() => {
    const checkPermissions = async () => {
      try {
        if (user?.role === 'admin') {
          setCanEdit(true);
          return;
        }
        
        const profiles = await base44.entities.OfficerProfile.list();
        const profile = profiles.find(p => p.user_email === user?.email);
        setCanEdit(profile?.custom_role === 'Commander');
      } catch (error) {
        console.error('Failed to check permissions:', error);
      }
    };
    if (user) checkPermissions();
  }, [user]);

  const isSuperAdmin = user?.role === 'admin';

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({ category: 'value', statement: '', elaboration: '' });
  const [editingId, setEditingId] = React.useState(null);

  const { data: immutables } = useQuery({
    queryKey: ['immutables'],
    queryFn: () => base44.entities.Immutable.list(),
    initialData: []
  });

  const createItemMutation = useMutation({
    mutationFn: (data) => base44.entities.Immutable.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['immutables'] });
      handleCloseDialog();
    }
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Immutable.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['immutables'] });
      handleCloseDialog();
    }
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id) => base44.entities.Immutable.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['immutables'] })
  });

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setFormData({ category: 'value', statement: '', elaboration: '' });
    setEditingId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      updateItemMutation.mutate({ id: editingId, data: formData });
    } else {
      const currentCategoryItems = immutables.filter(i => i.category === formData.category);
      const newOrder = currentCategoryItems.length;
      createItemMutation.mutate({ ...formData, severity: 'high', order: newOrder });
    }
  };

  const openEditDialog = (item) => {
    setFormData({ category: item.category, statement: item.statement, elaboration: item.elaboration || '' });
    setEditingId(item.id);
    setIsDialogOpen(true);
  };

  const getItemsByCategory = (category) => {
    return immutables
      .filter(i => i.category === category)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    
    const { source, destination } = result;
    
    // Only allow reordering within the same list/category
    if (source.droppableId !== destination.droppableId) return;
    
    if (source.index === destination.index) return;

    const category = source.droppableId;
    const items = Array.from(getItemsByCategory(category));
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    // Update order for all affected items
    items.forEach((item, index) => {
      if (item.order !== index) {
        updateItemMutation.mutate({ id: item.id, data: { order: index } });
      }
    });
  };

  const nobleWhy = immutables.find(i => i.category === 'noble_why');

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="space-y-12 pb-20">
        <div className="flex flex-col items-center space-y-4 relative">
          {canEdit && (
            <div className="absolute right-0 top-0">
              <Dialog open={isDialogOpen} onOpenChange={(open) => {
                if (open) setIsDialogOpen(true);
                else handleCloseDialog();
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-slate-700 hover:bg-slate-800 text-slate-400">
                    <PlusCircle className="w-4 h-4 mr-2" /> Add Rule
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="codex-codex-dialog" className="bg-slate-900 border-slate-800 text-slate-100">
                  <DialogHeader>
                    <DialogTitle>{editingId ? 'Edit Rule' : 'Amend The Codex'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={v => setFormData({...formData, category: v})}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="value">Core Value</SelectItem>
                          <SelectItem value="principle">Operating Principle</SelectItem>
                          <SelectItem value="company_red_line">Company Red Line</SelectItem>
                          <SelectItem value="people_red_line">People Red Line</SelectItem>
                          <SelectItem value="noble_why">Noble Raison D'Etre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Statement</Label>
                      <Input data-testid="codex-codex-input" 
                        value={formData.statement}
                        onChange={e => setFormData({...formData, statement: e.target.value})}
                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                        placeholder="The core rule..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Elaboration</Label>
                      <Textarea data-testid="codex-codex-textarea" 
                        value={formData.elaboration}
                        onChange={e => setFormData({...formData, elaboration: e.target.value})}
                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                        placeholder="Context and details..."
                      />
                    </div>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                      {editingId ? 'Update Rule' : 'Ratify Amendment'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <h1 className="text-4xl font-bold text-white tracking-tight">The Codex</h1>
          <p className="text-slate-400 max-w-xl mx-auto text-center">
            The immutable laws that govern our operations. 
            To violate these is to fail the game.
          </p>
          {!canEdit && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Lock className="w-3 h-3" />
              <span>View-only access. Contact a Commander to make changes.</span>
            </div>
          )}
        </div>

        {/* NOBLE WHY - NEW FORMAT */}
        <section className="relative py-20 overflow-hidden rounded-3xl border border-indigo-500/20 bg-slate-950 group">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/40 via-slate-950 to-slate-950" />
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?q=80&w=2013&auto=format&fit=crop')] opacity-10 bg-cover bg-center mix-blend-overlay" />
          
          <div className="relative z-10 flex flex-col items-center text-center px-6">
            <div className="mb-6 p-4 rounded-full bg-indigo-500/10 border border-indigo-500/30 shadow-[0_0_15px_rgba(99,102,241,0.3)]">
              <Heart className="w-8 h-8 text-indigo-400 fill-indigo-500/20" />
            </div>
            
            <h2 className="text-sm font-bold text-indigo-300 uppercase tracking-[0.3em] mb-6">Our North Star</h2>
            
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-black text-white leading-tight tracking-tight drop-shadow-2xl">
                {nobleWhy?.statement || 'The Noble Purpose'}
              </h1>
              
              <div className="mt-8 w-24 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent mx-auto" />
              
              {nobleWhy?.elaboration && (
                <p className="mt-8 text-xl md:text-2xl text-slate-300 font-light max-w-2xl mx-auto leading-relaxed">
                  {nobleWhy.elaboration}
                </p>
              )}
            </div>

            {canEdit && nobleWhy && (
              <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                <Button size="icon" variant="secondary" className="bg-slate-900/50 hover:bg-indigo-600 border border-slate-700 text-slate-200" onClick={() => openEditDialog(nobleWhy)}>
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* VALUES - KEEP GRID FORMAT */}
        <section className="space-y-8">
          <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
            <div className="p-2 rounded bg-cyan-500/10 text-cyan-400"><Shield className="w-5 h-5" /></div>
            <div>
              <h3 className="text-2xl font-bold text-white">Core Values</h3>
              <p className="text-sm text-slate-500">The behavioral pillars of our culture.</p>
            </div>
          </div>
          
          <Droppable droppableId="value" direction="horizontal" type="VALUE">
            {(provided) => (
              <div 
                ref={provided.innerRef} 
                {...provided.droppableProps}
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {getItemsByCategory('value').map((value, i) => (
                  <Draggable key={value.id} draggableId={value.id} index={i} isDragDisabled={!canEdit}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="h-full"
                      >
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.1 }}
                          className="group relative h-full"
                        >
                          <Card className="bg-slate-900/50 border-slate-800 hover:border-cyan-500/30 transition-all duration-300 hover:bg-slate-800/50 h-full flex flex-col">
                            <CardContent className="p-6 flex-1 flex flex-col relative">
                              {canEdit && (
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-cyan-400" onClick={() => openEditDialog(value)}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-400" onClick={() => deleteItemMutation.mutate(value.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                              <h4 className="text-xl font-bold text-slate-100 mb-3">{value.statement}</h4>
                              {value.elaboration && (
                                <p className="text-slate-400 leading-relaxed text-sm flex-1">{value.elaboration}</p>
                              )}
                              <div className="mt-4 pt-4 border-t border-slate-800/50">
                                <div className="w-full h-1 bg-cyan-900/30 rounded-full overflow-hidden">
                                  <div className="h-full bg-cyan-500/50 w-2/3" />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {getItemsByCategory('value').length === 0 && (
                  <div className="col-span-full py-12 text-center text-slate-500 italic border border-dashed border-slate-800 rounded-xl">
                    No values defined yet.
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </section>

        {/* PRINCIPLES - NEW LIST FORMAT */}
        <section className="space-y-8">
          <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
            <div className="p-2 rounded bg-teal-500/10 text-teal-400"><Scale className="w-5 h-5" /></div>
            <div>
              <h3 className="text-2xl font-bold text-white">Operating Principles</h3>
              <p className="text-sm text-slate-500">The 15-30 heuristic rules for decision making.</p>
            </div>
          </div>

          <Droppable droppableId="principle" type="PRINCIPLE">
            {(provided) => (
              <div 
                ref={provided.innerRef} 
                {...provided.droppableProps}
                className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-4"
              >
                {getItemsByCategory('principle').map((principle, i) => (
                  <Draggable key={principle.id} draggableId={principle.id} index={i} isDragDisabled={!canEdit}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <motion.div 
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className="group"
                        >
                          <div className="relative pl-12 py-4 border-b border-slate-800/50 hover:bg-slate-900/30 transition-colors rounded-lg pr-4">
                            {/* Number */}
                            <div className="absolute left-0 top-5 text-xs font-mono font-bold text-slate-600 group-hover:text-teal-500 transition-colors">
                              {String(i + 1).padStart(2, '0')}
                            </div>

                            <div className="flex justify-between items-start gap-4">
                              <div className="space-y-1">
                                <h4 className="font-bold text-slate-200 group-hover:text-white transition-colors text-lg">
                                  {principle.statement}
                                </h4>
                                {principle.elaboration && (
                                  <p className="text-sm text-slate-500 group-hover:text-slate-400 transition-colors leading-relaxed">
                                    {principle.elaboration}
                                  </p>
                                )}
                              </div>

                              {canEdit && (
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity self-start">
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-teal-400" onClick={() => openEditDialog(principle)}>
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-red-400" onClick={() => deleteItemMutation.mutate(principle.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {getItemsByCategory('principle').length === 0 && (
                   <div className="col-span-full py-12 text-center text-slate-500 italic border border-dashed border-slate-800 rounded-xl">
                    No operating principles defined yet.
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </section>

        {/* RED LINES */}
        <section className="space-y-6 pt-8">
          <div className="flex items-center gap-3 text-rose-500">
            <Flame className="w-6 h-6" />
            <h3 className="text-2xl font-bold">The Red Lines</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Company Red Lines</h4>
              <Droppable droppableId="company_red_line" type="RED_LINE">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4">
                    {getItemsByCategory('company_red_line').map((line, i) => (
                      <Draggable key={line.id} draggableId={line.id} index={i} isDragDisabled={!canEdit}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <div className="p-4 rounded border border-rose-900/30 bg-rose-950/10 flex gap-4 items-start group relative">
                              <AlertOctagon className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-rose-200 font-medium pr-16">{line.statement}</p>
                              </div>
                              {isSuperAdmin && (
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-300 hover:text-rose-100 hover:bg-rose-900/50" onClick={() => openEditDialog(line)}>
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-300 hover:text-rose-100 hover:bg-rose-900/50" onClick={() => deleteItemMutation.mutate(line.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
            <div className="space-y-4">
              <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider">People Red Lines</h4>
              <Droppable droppableId="people_red_line" type="RED_LINE">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4">
                    {getItemsByCategory('people_red_line').map((line, i) => (
                      <Draggable key={line.id} draggableId={line.id} index={i} isDragDisabled={!canEdit}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <div className="p-4 rounded border border-rose-900/30 bg-rose-950/10 flex gap-4 items-start group relative">
                              <AlertOctagon className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-rose-200 font-medium pr-16">{line.statement}</p>
                              </div>
                              {canEdit && (
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-300 hover:text-rose-100 hover:bg-rose-900/50" onClick={() => openEditDialog(line)}>
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-300 hover:text-rose-100 hover:bg-rose-900/50" onClick={() => deleteItemMutation.mutate(line.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </section>
      </div>
    </DragDropContext>
  );
}